'use client';

import * as React from 'react';
import {
  LineChart as MuiLineChart,
  LineChartProps as MuiLineChartProps,
} from '@mui/x-charts/LineChart';
import { Menu, MenuItem } from '@mui/material';

export interface LineChartWrapperProps extends MuiLineChartProps {}

export const LineChartWrapper: React.FC<LineChartWrapperProps> = props => {
  const chartRef = React.useRef<HTMLDivElement>(null);

  const [menuPos, setMenuPos] = React.useState<{ mouseX: number; mouseY: number } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setMenuPos({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleClose = () => setMenuPos(null);

  // Funzione comune per ottenere l'SVG clonato con gli stili applicati
  const getSvg = (): SVGSVGElement | null => {
    const svg = chartRef.current?.querySelector('svg');
    if (!svg) {
      console.error('SVG non trovato');
      return null;
    }

    // Clona l'SVG per non modificare il DOM originale
    const svgClone = svg.cloneNode(true) as SVGSVGElement;

    // Raccoglie tutti i CSS attivi nel documento
    let css = '';
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) {
          css += rule.cssText;
        }
      } catch (e) {
        // cross-origin sheet, ignora
      }
    }

    // Applica CSS globali e aggiunge override per assi e label neri
    css += `
      .MuiChartsAxis-line,
      .MuiChartsAxis-tick,
      .MuiChartsAxis-label,
      .MuiChartsAxis-tickLabel {
        fill: #000 !important;
      }
      .MuiChartsLegend-root,
      .MuiChartsLegend-root *,
      .MuiChartsLegend-root text,
      .MuiChartsLegend-root tspan {
        color: #000 !important;
      }
      .MuiChartsLegend-root text{
        fill: inherit !important;
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = css;
    svgClone.insertBefore(styleElement, svgClone.firstChild);

    return svgClone;
  };

  const handleExportSvg = () => {
    const svgClone = getSvg();
    if (!svgClone) return;

    // Serializza e scarica
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${props.title ?? 'chart'}.svg`;
    link.click();
    URL.revokeObjectURL(link.href);
    handleClose();
  };

  const handleExportPng = () => {
    const svgClone = getSvg();
    if (!svgClone) return;

    // Serializza SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Crea un'immagine dall'SVG
    const img = new Image();
    img.onload = () => {
      // Crea un canvas e disegna l'immagine
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return console.error('Impossibile creare il contesto del canvas');
      }

      // Sfondo bianco
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Disegna l'immagine
      ctx.drawImage(img, 0, 0);

      // Converti in PNG e scarica
      canvas.toBlob(blob => {
        if (!blob) {
          URL.revokeObjectURL(url);
          return console.error('Impossibile creare il blob PNG');
        }
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${props.title ?? 'chart'}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
        URL.revokeObjectURL(url);
        handleClose();
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      console.error("Errore nel caricamento dell'immagine SVG");
    };
    img.src = url;
  };

  return (
    <div ref={chartRef} onContextMenu={handleContextMenu} style={{ width: 'fit-content' }}>
      <MuiLineChart {...props} />

      <Menu
        open={menuPos !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}
      >
        <MenuItem onClick={handleExportSvg}>Scarica SVG</MenuItem>
        <MenuItem onClick={handleExportPng}>Scarica PNG</MenuItem>
      </Menu>
    </div>
  );
};
